import { create } from 'zustand';
import type {
  Channel,
  DmChannel,
  Friendship,
  Guild,
  Member,
  Message,
  PresenceStatus,
  Role,
  User,
  VoiceState,
} from '@callzone/shared';
import type { Track } from 'livekit-client';
import type { GuildBundle } from './lib/api';
import { getSettings, setSettings } from './lib/platform';

export type Phase = 'boot' | 'onboarding' | 'connecting' | 'ready';

export interface VoiceParticipant {
  identity: string;
  speaking: boolean;
  micMuted: boolean;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'unknown';
}

export interface ParticipantTracks {
  camera?: Track;
  screen?: Track;
}

export interface VoiceSession {
  guildId: string | null;
  channelId: string | null;
  status: 'idle' | 'connecting' | 'connected' | 'reconnecting';
  micEnabled: boolean;
  deafened: boolean;
  sharingScreen: boolean;
  cameraEnabled: boolean;
  canPublish: string[];
  /** Tile em foco no palco. null = grade. */
  focusedIdentity: string | null;
  error: string | null;
}

interface State {
  phase: Phase;
  serverUrl: string;
  token: string | null;
  me: User | null;
  connectionError: string | null;

  guilds: Record<string, GuildBundle>;
  guildOrder: string[];
  activeGuildId: string | null;
  activeChannelId: string | null;

  messages: Record<string, Message[]>;
  loadedChannels: Record<string, boolean>;
  presences: Record<string, PresenceStatus>;
  voiceStates: Record<string, VoiceState>;
  /** channelId -> userId -> timestamp do ultimo "digitando" */
  typing: Record<string, Record<string, number>>;

  /**
   * Apelidos que VOCE deu a outras pessoas: userId -> apelido.
   * Sao particulares — a outra pessoa continua se vendo com o nome dela.
   */
  aliases: Record<string, string>;

  /**
   * Volume de cada pessoa na call, de 0 a 1 (1 = normal). Ausente significa 1.
   * E uma preferencia sua: nao afeta o que os outros ouvem.
   */
  volumes: Record<string, number>;

  // ------------------------------------- amizade e conversa privada

  amizades: Friendship[];
  conversas: DmChannel[];
  /** dmChannelId -> mensagens, do mais antigo para o mais novo. */
  dmMessages: Record<string, Message[]>;
  /**
   * Conversa privada aberta. Quando tem valor, a area principal mostra o
   * privado em vez do canal da guild.
   */
  activeDmId: string | null;
  /** Ligacao privada chegando: quem esta chamando e de qual conversa. */
  chamando: { channelId: string; fromUserId: string } | null;
  /** Painel de amigos aberto no lugar da lista de canais. */
  painelAmigos: boolean;

  voice: VoiceSession;
  voiceParticipants: Record<string, VoiceParticipant>;
  tracks: Record<string, ParticipantTracks>;

  toasts: Array<{ id: number; text: string; kind: 'info' | 'error' }>;
}

interface Actions {
  setPhase: (phase: Phase) => void;
  setConnection: (serverUrl: string, token: string | null) => void;
  setMe: (user: User) => void;
  setConnectionError: (message: string | null) => void;

  applyReady: (payload: {
    user: User;
    guilds: GuildBundle[];
    voiceStates: VoiceState[];
    presences: Array<{ userId: string; status: PresenceStatus }>;
  }) => void;

  selectGuild: (guildId: string) => void;
  selectChannel: (channelId: string) => void;

  upsertMessage: (message: Message) => void;
  removeMessage: (channelId: string, messageId: string) => void;
  setMessages: (channelId: string, messages: Message[]) => void;
  prependMessages: (channelId: string, messages: Message[]) => void;

  setAliases: (aliases: Record<string, string>) => void;
  setAlias: (userId: string, alias: string | null) => void;
  setVolume: (userId: string, volume: number) => void;

  setAmizades: (amizades: Friendship[]) => void;
  setConversas: (conversas: DmChannel[]) => void;
  setDmMessages: (channelId: string, messages: Message[]) => void;
  upsertDmMessage: (message: Message) => void;
  selectDm: (channelId: string | null) => void;
  setChamando: (chamada: { channelId: string; fromUserId: string } | null) => void;
  setPainelAmigos: (aberto: boolean) => void;

  setPresence: (userId: string, status: PresenceStatus) => void;
  setVoiceState: (state: VoiceState) => void;
  markTyping: (channelId: string, userId: string) => void;

  upsertChannel: (channel: Channel) => void;
  removeChannel: (guildId: string, channelId: string) => void;
  upsertRole: (role: Role) => void;
  removeRole: (guildId: string, roleId: string) => void;
  upsertMember: (member: Member) => void;
  /** Substitui os dados de uma pessoa em todas as guilds onde ela aparece. */
  upsertUser: (user: User) => void;
  removeMember: (guildId: string, userId: string) => void;
  updateGuild: (guild: Guild) => void;

  patchVoice: (patch: Partial<VoiceSession>) => void;
  setVoiceParticipant: (participant: VoiceParticipant) => void;
  removeVoiceParticipant: (identity: string) => void;
  setTrack: (identity: string, kind: keyof ParticipantTracks, track: Track | undefined) => void;
  clearVoice: () => void;

  toast: (text: string, kind?: 'info' | 'error') => void;
  dismissToast: (id: number) => void;
  reset: () => void;
}

const emptyVoice: VoiceSession = {
  guildId: null,
  channelId: null,
  status: 'idle',
  micEnabled: true,
  deafened: false,
  sharingScreen: false,
  cameraEnabled: false,
  canPublish: [],
  focusedIdentity: null,
  error: null,
};

let toastId = 0;

export const useStore = create<State & Actions>((set, get) => ({
  phase: 'boot',
  serverUrl: '',
  token: null,
  me: null,
  connectionError: null,

  guilds: {},
  guildOrder: [],
  activeGuildId: null,
  activeChannelId: null,

  messages: {},
  loadedChannels: {},
  presences: {},
  voiceStates: {},
  typing: {},
  aliases: {},
  // Volumes sao preferencia local e sobrevivem ao recarregar a pagina.
  volumes: getSettings().volumes as Record<string, number> ?? {},

  amizades: [],
  conversas: [],
  dmMessages: {},
  activeDmId: null,
  chamando: null,
  painelAmigos: false,

  voice: emptyVoice,
  voiceParticipants: {},
  tracks: {},
  toasts: [],

  setPhase: (phase) => set({ phase }),
  setConnection: (serverUrl, token) => set({ serverUrl, token }),
  setMe: (me) => set({ me }),
  setConnectionError: (connectionError) => set({ connectionError }),

  applyReady: ({ user, guilds, voiceStates, presences }) => {
    const guildMap: Record<string, GuildBundle> = {};
    for (const bundle of guilds) guildMap[bundle.guild.id] = bundle;

    const state = get();
    const order = guilds.map((g) => g.guild.id);
    // Mantem a guild/canal aberto se ainda existirem apos a atualizacao.
    const activeGuildId =
      state.activeGuildId && guildMap[state.activeGuildId] ? state.activeGuildId : (order[0] ?? null);
    const activeGuild = activeGuildId ? guildMap[activeGuildId] : undefined;
    const channelStillThere =
      state.activeChannelId && activeGuild?.channels.some((c) => c.id === state.activeChannelId);
    const activeChannelId = channelStillThere
      ? state.activeChannelId
      : (activeGuild?.channels.find((c) => c.type === 'text')?.id ?? null);

    set({
      me: user,
      guilds: guildMap,
      guildOrder: order,
      activeGuildId,
      activeChannelId,
      voiceStates: Object.fromEntries(voiceStates.map((v) => [v.userId, v])),
      presences: Object.fromEntries(presences.map((p) => [p.userId, p.status])),
      phase: 'ready',
      connectionError: null,
    });
  },

  selectGuild: (guildId) => {
    const bundle = get().guilds[guildId];
    if (!bundle) return;
    const firstText = bundle.channels.find((c) => c.type === 'text');
    set({ activeGuildId: guildId, activeChannelId: firstText?.id ?? null });
  },

  // Abrir um canal fecha a conversa privada: a area principal e uma so.
  selectChannel: (channelId) => set({ activeChannelId: channelId, activeDmId: null, painelAmigos: false }),

  upsertMessage: (message) =>
    set((state) => {
      const list = state.messages[message.channelId] ?? [];
      const index = list.findIndex((m) => m.id === message.id);
      const next = index === -1 ? [...list, message] : list.map((m) => (m.id === message.id ? message : m));
      return { messages: { ...state.messages, [message.channelId]: next } };
    }),

  removeMessage: (channelId, messageId) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [channelId]: (state.messages[channelId] ?? []).filter((m) => m.id !== messageId),
      },
    })),

  setMessages: (channelId, messages) =>
    set((state) => ({
      messages: { ...state.messages, [channelId]: messages },
      loadedChannels: { ...state.loadedChannels, [channelId]: true },
    })),

  prependMessages: (channelId, messages) =>
    set((state) => ({
      messages: { ...state.messages, [channelId]: [...messages, ...(state.messages[channelId] ?? [])] },
    })),

  setAliases: (aliases) => set({ aliases }),

  setAlias: (userId, alias) =>
    set((state) => {
      const aliases = { ...state.aliases };
      if (alias) aliases[userId] = alias;
      else delete aliases[userId];
      return { aliases };
    }),

  setVolume: (userId, volume) =>
    set((state) => {
      const volumes = { ...state.volumes, [userId]: Math.max(0, Math.min(1, volume)) };
      setSettings({ ...getSettings(), volumes });
      return { volumes };
    }),

  setAmizades: (amizades) => set({ amizades }),
  setConversas: (conversas) => set({ conversas }),

  setDmMessages: (channelId, messages) =>
    set((state) => ({ dmMessages: { ...state.dmMessages, [channelId]: messages } })),

  upsertDmMessage: (message) =>
    set((state) => {
      const atuais = state.dmMessages[message.channelId] ?? [];
      // O autor tambem recebe o proprio evento: sem esta checagem a mensagem
      // apareceria duas vezes na tela de quem enviou.
      if (atuais.some((m) => m.id === message.id)) return {};
      return {
        dmMessages: { ...state.dmMessages, [message.channelId]: [...atuais, message] },
      };
    }),

  /* Abrir uma conversa privada tira o canal da guild da area principal. */
  selectDm: (channelId) => set({ activeDmId: channelId, painelAmigos: true }),
  setChamando: (chamando) => set({ chamando }),
  setPainelAmigos: (painelAmigos) => set({ painelAmigos }),

  setPresence: (userId, status) =>
    set((state) => ({ presences: { ...state.presences, [userId]: status } })),

  setVoiceState: (voiceState) =>
    set((state) => {
      const next = { ...state.voiceStates };
      if (voiceState.channelId === null) delete next[voiceState.userId];
      else next[voiceState.userId] = voiceState;
      return { voiceStates: next };
    }),

  markTyping: (channelId, userId) =>
    set((state) => ({
      typing: {
        ...state.typing,
        [channelId]: { ...(state.typing[channelId] ?? {}), [userId]: Date.now() },
      },
    })),

  upsertChannel: (channel) =>
    set((state) => {
      const bundle = state.guilds[channel.guildId];
      if (!bundle) return {};
      const exists = bundle.channels.some((c) => c.id === channel.id);
      const channels = exists
        ? bundle.channels.map((c) => (c.id === channel.id ? channel : c))
        : [...bundle.channels, channel];
      return { guilds: { ...state.guilds, [channel.guildId]: { ...bundle, channels } } };
    }),

  removeChannel: (guildId, channelId) =>
    set((state) => {
      const bundle = state.guilds[guildId];
      if (!bundle) return {};
      const channels = bundle.channels.filter((c) => c.id !== channelId);
      const activeChannelId =
        state.activeChannelId === channelId
          ? (channels.find((c) => c.type === 'text')?.id ?? null)
          : state.activeChannelId;
      return { guilds: { ...state.guilds, [guildId]: { ...bundle, channels } }, activeChannelId };
    }),

  upsertRole: (role) =>
    set((state) => {
      const bundle = state.guilds[role.guildId];
      if (!bundle) return {};
      const exists = bundle.roles.some((r) => r.id === role.id);
      const roles = exists
        ? bundle.roles.map((r) => (r.id === role.id ? role : r))
        : [...bundle.roles, role];
      roles.sort((a, b) => b.position - a.position);
      return { guilds: { ...state.guilds, [role.guildId]: { ...bundle, roles } } };
    }),

  removeRole: (guildId, roleId) =>
    set((state) => {
      const bundle = state.guilds[guildId];
      if (!bundle) return {};
      return {
        guilds: {
          ...state.guilds,
          [guildId]: { ...bundle, roles: bundle.roles.filter((r) => r.id !== roleId) },
        },
      };
    }),

  upsertMember: (member) =>
    set((state) => {
      const bundle = state.guilds[member.guildId];
      if (!bundle) return {};
      const exists = bundle.members.some((m) => m.userId === member.userId);
      const members = exists
        ? bundle.members.map((m) => (m.userId === member.userId ? member : m))
        : [...bundle.members, member];
      return { guilds: { ...state.guilds, [member.guildId]: { ...bundle, members } } };
    }),

  /*
   * A mesma pessoa aparece como membro em varias guilds. Trocar a foto ou o
   * nome tem de refletir em todas, senao o avatar novo apareceria numa e o
   * antigo continuaria em outra.
   */
  upsertUser: (user) =>
    set((state) => {
      const guilds: typeof state.guilds = {};
      let mudou = false;
      for (const [id, bundle] of Object.entries(state.guilds)) {
        if (!bundle.members.some((m) => m.userId === user.id)) {
          guilds[id] = bundle;
          continue;
        }
        mudou = true;
        guilds[id] = {
          ...bundle,
          members: bundle.members.map((m) => (m.userId === user.id ? { ...m, user } : m)),
        };
      }
      return mudou ? { guilds } : {};
    }),

  removeMember: (guildId, userId) =>
    set((state) => {
      const bundle = state.guilds[guildId];
      if (!bundle) return {};
      return {
        guilds: {
          ...state.guilds,
          [guildId]: { ...bundle, members: bundle.members.filter((m) => m.userId !== userId) },
        },
      };
    }),

  updateGuild: (guild) =>
    set((state) => {
      const bundle = state.guilds[guild.id];
      if (!bundle) return {};
      return { guilds: { ...state.guilds, [guild.id]: { ...bundle, guild } } };
    }),

  patchVoice: (patch) => set((state) => ({ voice: { ...state.voice, ...patch } })),

  setVoiceParticipant: (participant) =>
    set((state) => ({
      voiceParticipants: { ...state.voiceParticipants, [participant.identity]: participant },
    })),

  removeVoiceParticipant: (identity) =>
    set((state) => {
      const participants = { ...state.voiceParticipants };
      const tracks = { ...state.tracks };
      delete participants[identity];
      delete tracks[identity];
      return { voiceParticipants: participants, tracks };
    }),

  setTrack: (identity, kind, track) =>
    set((state) => {
      const current = { ...(state.tracks[identity] ?? {}) };
      if (track) current[kind] = track;
      else delete current[kind];
      return { tracks: { ...state.tracks, [identity]: current } };
    }),

  clearVoice: () =>
    set({ voice: { ...emptyVoice }, voiceParticipants: {}, tracks: {} }),

  toast: (text, kind = 'info') =>
    set((state) => ({ toasts: [...state.toasts, { id: ++toastId, text, kind }] })),

  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

  reset: () =>
    set({
      phase: 'onboarding',
      token: null,
      me: null,
      guilds: {},
      guildOrder: [],
      activeGuildId: null,
      activeChannelId: null,
      messages: {},
      loadedChannels: {},
      presences: {},
      voiceStates: {},
      typing: {},
      aliases: {},
      amizades: [],
      conversas: [],
      dmMessages: {},
      activeDmId: null,
      chamando: null,
      painelAmigos: false,
      voice: { ...emptyVoice },
      voiceParticipants: {},
      tracks: {},
    }),
}));

// ------------------------------------------------------------- seletores

export function useActiveGuild() {
  return useStore((s) => (s.activeGuildId ? s.guilds[s.activeGuildId] : undefined));
}

export function useActiveChannel() {
  return useStore((s) => {
    if (!s.activeGuildId || !s.activeChannelId) return undefined;
    return s.guilds[s.activeGuildId]?.channels.find((c) => c.id === s.activeChannelId);
  });
}

/** Nome exibido: apelido da guild se houver, senao o do usuario. */
/**
 * Nome a mostrar, na ordem: o apelido particular que voce deu, depois o apelido
 * da guild, depois o nome da conta.
 *
 * Le os apelidos do estado em vez de recebe-los por parametro para nao ter de
 * mudar as dezenas de lugares que chamam esta funcao. Em troca, quem exibe nome
 * precisa assinar a fatia com `useAliases()`, senao nao re-renderiza quando um
 * apelido muda.
 */
export function displayName(bundle: GuildBundle | undefined, userId: string): string {
  const alias = useStore.getState().aliases[userId];
  if (alias) return alias;
  const member = bundle?.members.find((m) => m.userId === userId);
  return member?.nickname ?? member?.user.nickname ?? 'Desconhecido';
}

/** Assina a fatia de apelidos. Chame em quem desenha nomes na tela. */
export function useAliases(): Record<string, string> {
  return useStore((s) => s.aliases);
}

/** Nome real da conta, ignorando apelidos. Usado ao editar o apelido. */
export function originalName(bundle: GuildBundle | undefined, userId: string): string {
  const member = bundle?.members.find((m) => m.userId === userId);
  return member?.nickname ?? member?.user.nickname ?? 'Desconhecido';
}

/** Cor do cargo mais alto que tenha cor definida. */
export function memberColor(bundle: GuildBundle | undefined, userId: string): string | null {
  if (!bundle) return null;
  const member = bundle.members.find((m) => m.userId === userId);
  if (!member) return null;
  const colored = bundle.roles
    .filter((r) => member.roleIds.includes(r.id) && r.color)
    .sort((a, b) => b.position - a.position);
  return colored[0]?.color ?? null;
}

export function memberAccent(bundle: GuildBundle | undefined, userId: string): string {
  const member = bundle?.members.find((m) => m.userId === userId);
  return member?.user.accentColor ?? '#FF7A18';
}

/** Versao da foto de perfil. 0 significa que a pessoa nao tem foto. */
export function memberAvatarVersion(bundle: GuildBundle | undefined, userId: string): number {
  const member = bundle?.members.find((m) => m.userId === userId);
  return member?.user.avatarVersion ?? 0;
}
